import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEO from '@/components/feature/SEO';
import { categories, articles as allArticles } from '@/mocks/articles';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-900">分类不存在</h1>
            <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600 text-sm">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryArticles = allArticles.filter((a) => a.category === category.id);

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 911爆料网`,
    description: category.description,
    url: `http://baoliaowang.cn/category/${category.slug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: categoryArticles.map((article, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `http://baoliaowang.cn/article/${article.slug}`,
      })),
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'http://baoliaowang.cn/' },
      { '@type': 'ListItem', position: 2, name: category.name, item: `http://baoliaowang.cn/category/${category.slug}` },
    ],
  };

  return (
    <>
      <SEO
        title={`${category.name}`}
        description={category.description}
        keywords={`911爆料网,${category.name},吃瓜黑料,网红翻车,实时爆料`}
        canonicalUrl={`http://baoliaowang.cn/category/${category.slug}`}
        schema={{ ...categorySchema, ...breadcrumbSchema }}
      />
      <Navbar />
      <main>
        <section className="bg-gradient-to-br from-primary-900 via-primary-800 to-primary-950 pt-24 pb-16 md:pb-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: category.name },
              ]}
            />
            <div className="flex flex-col lg:flex-row items-start gap-8 md:gap-12 mt-8">
              <div className="lg:w-[55%]">
                <span className="inline-block px-3 py-1 bg-accent-100/20 text-accent-300 text-xs font-semibold rounded-full mb-4 whitespace-nowrap">
                  / {category.name}
                </span>
                <h1 className="font-heading text-3xl md:text-5xl font-black text-background-50 leading-tight">
                  {category.name}
                </h1>
                <p className="mt-4 text-background-50/60 text-sm md:text-base leading-relaxed max-w-lg">
                  {category.description}
                </p>
                <p className="mt-2 text-background-50/40 text-sm">
                  共 {categoryArticles.length} 条相关爆料
                </p>
              </div>
              <div className="lg:w-[45%] flex items-center gap-6">
                <div className="w-28 h-28 md:w-36 md:h-36 rounded-xl overflow-hidden shrink-0 bg-background-50/10">
                  <img
                    src={category.imageUrl}
                    alt={category.name}
                    title={`911爆料网 ${category.name}`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div>
                  <span className="font-heading text-5xl md:text-7xl font-black text-background-50">
                    999+
                  </span>
                  <p className="mt-2 text-background-50/60 text-sm leading-relaxed">
                    全网最全{category.name}爆料合集，每日持续更新中
                  </p>
                  <p className="mt-1 text-background-50/40 text-xs">
                    深度追踪，不遗漏任何细节
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-10 md:mb-14">
              <span className="inline-block px-3 py-1 bg-secondary-100 text-secondary-900 text-xs font-semibold rounded-full mb-3 whitespace-nowrap">
                最新爆料
              </span>
              <h2 className="font-heading text-2xl md:text-4xl font-black text-foreground-950">
                全网热瓜
                <br />
                每日更新
              </h2>
            </div>

            <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 md:gap-6">
              {categoryArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.slug}`}
                  className="block mb-5 md:mb-6 break-inside-avoid group"
                >
                  <div className="bg-background-50 rounded-xl overflow-hidden transition-all duration-300 group-hover:translate-y-[-4px]">
                    <div className="flex items-start gap-3 p-4 md:p-5">
                      <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center shrink-0">
                        <span className="text-primary-600 text-xs font-bold">{article.author.charAt(0)}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-heading font-bold text-foreground-900 text-sm leading-snug group-hover:text-primary-500 transition-colors">
                          {article.title}
                        </h3>
                        <p className="text-foreground-500 text-xs mt-1">@{article.author}</p>
                      </div>
                    </div>
                    <div className="px-4 md:px-5 pb-4 md:pb-5">
                      <p className="text-foreground-600 text-sm leading-relaxed line-clamp-3">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center gap-3 mt-3 text-xs text-foreground-400">
                        <span>{article.date}</span>
                        <span>·</span>
                        <span>{article.views.toLocaleString()} 阅读</span>
                        {article.isExclusive && (
                          <>
                            <span>·</span>
                            <span className="text-accent-500 font-semibold">独家</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}